import { CheckIcon } from "@phosphor-icons/react/Check";
import { Banner } from "@freightsimple/ui-core/Display/Banner";
import { T } from "@freightsimple/ui-core/Typography/T";

interface UploadPhotosUploadResultProps {
  /** Photos attached to the shipment during this visit to the page. */
  attachedCount: number;
  /** Set when the latest photo failed to upload; otherwise it was attached. */
  failureMessage: string | undefined;
}

function describeAttachedCount(count: number): string {
  return count === 1
    ? "1 photo attached so far."
    : `${count} photos attached so far.`;
}

/**
 * Tells the shipper whether their latest photo reached the shipment. Success
 * is a quiet line, since the photo itself already shows a tick; a failure is
 * a full red banner because the shipper has to act on it.
 */
export function UploadPhotosUploadResult(props: UploadPhotosUploadResultProps) {
  if (props.failureMessage !== undefined) {
    return (
      <div
        role="alert"
        data-testid="upload-photos-failed-banner"
        className="w-full"
      >
        <Banner variant="danger" title="Photo not attached">
          {props.failureMessage}
        </Banner>
      </div>
    );
  }

  return (
    <div
      role="status"
      data-testid="upload-photos-attached-message"
      className="flex flex-wrap items-center gap-x-2 gap-y-1"
    >
      <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-dark-green text-white">
        <CheckIcon weight="bold" size={12} aria-hidden />
      </span>
      <T size="sm" weight="semibold">
        Photo attached to your shipment
      </T>
      <T size="sm" color="secondary">
        {describeAttachedCount(props.attachedCount)}
      </T>
    </div>
  );
}
